<template>
  <b-card>
    <!-- PDF Configuration Controls (for debugging) - Remove this section for production -->
    <div
      v-if="false"
      class="pdf-config-controls mb-3 p-3 border rounded"
      style="background-color: #f8f9fa;"
    >
      <h6>PDF Configuration</h6>
      <div class="row">
        <div class="col-md-3">
          <label class="small">Zoom ({{ pdfConfig.zoom }})</label>
          <input
            v-model.number="pdfConfig.zoom"
            type="range"
            min="0.5"
            max="2.0"
            step="0.05"
            class="form-control-range"
            @input="updatePdfConfig({ zoom: pdfConfig.zoom })"
          >
        </div>
        <div class="col-md-3">
          <label class="small">Scale ({{ pdfConfig.scale }})</label>
          <input
            v-model.number="pdfConfig.scale"
            type="range"
            min="1"
            max="5"
            step="1"
            class="form-control-range"
            @input="updatePdfConfig({ scale: pdfConfig.scale })"
          >
        </div>
        <div class="col-md-3">
          <label class="small">Margin ({{ pdfConfig.margin }}px)</label>
          <input
            v-model.number="pdfConfig.margin"
            type="range"
            min="0"
            max="20"
            step="2"
            class="form-control-range"
            @input="updatePdfConfig({ margin: pdfConfig.margin })"
          >
        </div>
        <div class="col-md-3">
          <button
            class="btn btn-sm btn-outline-primary"
            @click="generatePDF()"
          >
            Regenerate PDF
          </button>
        </div>
      </div>
    </div>

    <div class="pdf-container">
      <iframe
        v-if="pdfUrl"
        :src="pdfUrl"
        class="pdf-viewer"
        frameborder="0"
        scrolling="no"
        seamless
      />
      <div
        v-else
        class="text-center d-flex flex-column justify-content-center align-items-center"
        style="height: 500px"
      >
        <b-spinner variant="primary" />
        <p class="mt-50">
          Memuat...
        </p>
      </div>
    </div>

    <!-- Hidden content for PDF generation -->
    <div
      id="pdf-content"
      class="pdf-hidden-content"
      :style="{ padding: `${pdfConfig.margin}px` }"
    >
      <div
        class="label-container"
        :class="size"
      >
        <!-- Header -->
        <div class="label-header">
          <div class="header-left">
            <img
              :src="logo"
              alt="logo"
              class="logo"
            >
            <p class="invoice-number">
              {{ data.invoice_number }}
            </p>
          </div>
          <div class="header-right">
            <div class="courier-name">
              {{ data.delivery.courier }}
            </div>
            <div class="service-name">
              {{ data.delivery.service }}
            </div>
            <div
              v-if="data.delivery_insurance_fee"
              class="insurance"
            >
              <small>Asuransi<span v-if="showInsuranceFee">: {{ formatCurrency(data.delivery_insurance_fee) }}</span></small>
            </div>
          </div>
        </div>
        <hr class="spacer">

        <!-- Barcode Section -->
        <template v-if="showBarcode && data.delivery.airway_bill">
          <div class="barcode-section">
            <div class="barcode-label">
              Airwaybill Number
            </div>
            <barcode
              :value="data.delivery.airway_bill"
              width="2"
              height="40"
              font-size="14"
            />
          </div>
          <hr class="spacer mt-25">
        </template>

        <!-- Main Content -->
        <div class="main-content">
          <div class="address-section">
            <div class="to-address">
              <h6>Kepada:</h6>
              <div class="recipient-name">
                {{ data.delivery.consignee_name }}
              </div>
              <div class="recipient-address">
                {{ data.delivery.fullAddress }}
              </div>
            </div>
            <div class="from-address">
              <h6>Dari:</h6>
              <div class="sender-name">
                {{ data.merchant.name }}
              </div>
              <div class="sender-address">
                {{ data.merchant.address.district_name }}, {{ data.merchant.address.city_name }}
              </div>
              <small>081286867893</small>
            </div>
          </div>

          <!-- Products Table -->
          <template v-if="showProduct">
            <hr class="spacer">
            <div class="products-section">
              <table class="products-table">
                <thead>
                  <tr>
                    <th>Produk</th>
                    <th>Jumlah</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in data.items"
                    :key="index"
                  >
                    <td>
                      <div class="product-item">
                        <span class="item-number">{{ index + 1 }}.</span>
                        <div class="product-details">
                          <div class="product-name">
                            {{ item.product.name }}
                          </div>
                          <div
                            v-if="item.attribute_concat"
                            class="product-attr"
                          >
                            {{ item.attribute_concat }}
                          </div>
                          <div
                            v-if="item.sku_name"
                            class="product-sku"
                          >
                            ({{ item.sku_name }})
                          </div>
                        </div>
                      </div>
                    </td>
                    <td class="quantity">
                      {{ item.qty }} Pcs
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <!-- Note -->
          <div class="note-section">
            <div
              v-if="data.use_gift_wrap"
              class="gift-wrap-container"
            >
              <feather-icon
                icon="GiftIcon"
                size="16"
                style="margin-right: 8px;"
              />
              <span><strong>Penjual wajib</strong> membungkus kado</span>
            </div>
            <p>Catatan: Wajib merekam proses unboxing untuk mengajukan komplain.</p>
          </div>
        </div>
      </div>
    </div>
  </b-card>
</template>

<script>
import {
  BCard,
  BSpinner,
} from 'bootstrap-vue'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { $themeConfig } from '@themeConfig'
import barcode from 'vue-barcode'

export default {
  components: {
    barcode,
    BCard,
    BSpinner,
  },
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    size: {
      type: String,
      default: 'a4',
    },
    showProduct: {
      type: Boolean,
      default: true,
    },
    showBarcode: {
      type: Boolean,
      default: true,
    },
    showInsuranceFee: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      pdfUrl: null,
      logo: $themeConfig.app.appLogoImage,
      // Configuration variables for easy customization
      pdfConfig: {
        scale: 3, // Controls resolution/DPI (higher = better quality, 2-5 recommended)
        zoom: 0.8, // Controls content size/zoom (0.5 = smaller, 1.5 = larger) - reduced due to larger fonts
        margin: 12, // Margin between paper and dotted border (in pixels)
        paperWidthMm: 105, // A6 width in mm
        useIframePrint: true, // Try iframe printing first (faster), fallback to direct PDF generation
      },
    }
  },
  computed: {
    hideCourier() {
      return [
        '94a909cf315647ca8b72d39ccd089abd',
        '967e13bca5544f2d9d3ce1241a5ae702',
        '94a90a0482854479ae1a5aae5fdf5e53',
        '97d02d9a03b04faf96a95f7418cc7ae6',
        '978baf73fb014f809ce6b2c276819147',
        '94ff5a4363f74892aa1b25d9533ed03f',
        '9777520af1c0404695a079ca848995f4',
        '9777529d6d5d47e79a625041642017d3',
        '9742e31ae1194970b04dcd4f9c2ae38f',
        '952e0f8de55d48f69831b60b9c975e43',
      ]
        .includes(this.data.merchant?.id)
    },
  },
  watch: {
    data: {
      handler() {
        if (this.data && this.data.delivery) {
          this.$nextTick(() => {
            this.generatePDF()
          })
        }
      },
      immediate: false, // Don't run immediately, let mounted handle initial generation
      deep: true,
    },
    size() {
      if (this.data && this.data.delivery) {
        this.$nextTick(() => {
          this.generatePDF()
        })
      }
    },
    showProduct() {
      if (this.data && this.data.delivery) {
        this.$nextTick(() => {
          this.generatePDF()
        })
      }
    },
    showBarcode() {
      if (this.data && this.data.delivery) {
        this.$nextTick(() => {
          this.generatePDF()
        })
      }
    },
    showInsuranceFee() {
      if (this.data && this.data.delivery) {
        this.$nextTick(() => {
          this.generatePDF()
        })
      }
    },
  },
  mounted() {
    // Generate PDF after component is mounted and DOM is ready
    if (this.data && this.data.delivery) {
      this.$nextTick(() => {
        this.generatePDF()
      })
    }
  },
  methods: {
    async generatePDF() {
      if (!this.data || !this.data.delivery) {
        return
      }

      try {
        // Clear previous PDF URL
        if (this.pdfUrl) {
          URL.revokeObjectURL(this.pdfUrl)
          this.pdfUrl = null
        }

        // Get the hidden content element with retry logic
        const contentElement = document.getElementById('pdf-content')
        if (!contentElement) {
          // Retry after a short delay
          setTimeout(() => {
            this.generatePDF()
          }, 100)
          return
        } // Calculate proper dimensions based on paper size
        const targetWidthMm = this.size === 'a4' ? 105 : 105 // A4 uses half width (105mm), A6 uses full width (105mm)

        // Calculate container width to accommodate zoom and margin without cropping
        // Subtract margin on both sides (margin * 2) and convert to mm
        const marginMm = (this.pdfConfig.margin * 2) / 3.78
        const containerWidthMm = (targetWidthMm - marginMm) / this.pdfConfig.zoom

        // Reset and apply styling
        contentElement.style.fontSize = ''
        contentElement.style.transform = ''
        contentElement.style.transformOrigin = 'top left'
        contentElement.style.width = `${containerWidthMm}mm`

        // Apply zoom scaling
        if (this.pdfConfig.zoom !== 1) {
          contentElement.style.transform = `scale(${this.pdfConfig.zoom})`
        }

        // Wait for images to load and DOM to settle
        await this.waitForImages(contentElement)
        await new Promise(resolve => setTimeout(resolve, 100)) // Small delay for DOM settling

        // Convert to canvas with proper width calculation
        const canvasWidthPx = containerWidthMm * 3.78 * this.pdfConfig.zoom

        const canvas = await html2canvas(contentElement, {
          scale: this.pdfConfig.scale,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: canvasWidthPx,
          height: undefined, // Let height be determined by content
        })

        // Create PDF with appropriate size
        // eslint-disable-next-line new-cap
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: this.size === 'a4' ? 'a4' : [105, 148],
        })

        const imgData = canvas.toDataURL('image/png')
        const contentHeightMm = (canvas.height / (this.pdfConfig.scale * 3.78))

        // Place content appropriately based on paper size
        if (this.size === 'a4') {
          // A4: Place content in top-left, taking only half the width (105mm out of 210mm)
          pdf.addImage(imgData, 'PNG', 0, 0, targetWidthMm, contentHeightMm)
        } else {
          // A6: Use full page
          pdf.addImage(imgData, 'PNG', 0, 0, targetWidthMm, contentHeightMm)
        }

        // Create blob URL with PDF viewer options to hide all UI elements
        const pdfBlob = pdf.output('blob')
        const pdfUrl = URL.createObjectURL(pdfBlob)
        this.pdfUrl = `${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0`
      } catch (error) {
        alert('Error generating PDF:', error)
      }
    },

    async waitForImages(container) {
      const images = container.querySelectorAll('img')
      const imagePromises = Array.from(images).map(img => {
        if (img.complete) return Promise.resolve()
        return new Promise((resolve, reject) => {
          // eslint-disable-next-line no-param-reassign
          img.onload = resolve
          // eslint-disable-next-line no-param-reassign
          img.onerror = reject
          setTimeout(resolve, 5000)
        })
      })
      await Promise.all(imagePromises)
    },

    getPhone(phone) {
      if (!phone) {
        return null
      }

      let phoneNew = phone

      if (phoneNew[0] === '6' && phoneNew[1] === '2') {
        phoneNew = phoneNew.substr(2, phoneNew.length - 2)
        phoneNew = `0${phoneNew}`
      }

      return phoneNew
    },

    formatCurrency(value) {
      if (!value) return 'Rp 0'
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
      }).format(value)
    },

    // Helper method to update PDF configuration and regenerate
    updatePdfConfig(newConfig) {
      this.pdfConfig = { ...this.pdfConfig, ...newConfig }
      if (this.data && this.data.delivery) {
        this.generatePDF()
      }
    },

    // Method to print the current PDF directly without new tab
    async printPDF() {
      if (!this.data || !this.data.delivery) {
        return
      }

      try {
        // Generate PDF directly for printing
        const pdf = await this.generatePrintablePDF()
        if (pdf) {
          // Use jsPDF's autoPrint functionality
          pdf.autoPrint()

          // Create a hidden iframe and print
          const pdfBlob = pdf.output('blob')
          const pdfUrl = URL.createObjectURL(pdfBlob)

          // Create hidden iframe for printing
          const iframe = document.createElement('iframe')
          iframe.style.position = 'fixed'
          iframe.style.right = '0'
          iframe.style.bottom = '0'
          iframe.style.width = '0'
          iframe.style.height = '0'
          iframe.style.border = 'none'

          document.body.appendChild(iframe)

          iframe.onload = () => {
            setTimeout(() => {
              try {
                iframe.contentWindow.print()
              } catch (e) {
                // Fallback: open in new window if iframe printing fails
                window.open(pdfUrl, '_blank')
              }

              // Clean up
              setTimeout(() => {
                document.body.removeChild(iframe)
                URL.revokeObjectURL(pdfUrl)
              }, 1000)
            }, 100)
          }

          iframe.src = `${pdfUrl}#print`
        }
      } catch (error) {
        // Fallback to regular print
        window.print()
      }
    },

    // Generate PDF specifically for printing (without storing in pdfUrl)
    async generatePrintablePDF() {
      try {
        const contentElement = document.getElementById('pdf-content')
        if (!contentElement) {
          return null
        }

        // Calculate dimensions
        const targetWidthMm = this.size === 'a4' ? 105 : 105
        const marginMm = (this.pdfConfig.margin * 2) / 3.78
        const containerWidthMm = (targetWidthMm - marginMm) / this.pdfConfig.zoom

        // Apply styling
        contentElement.style.width = `${containerWidthMm}mm`
        contentElement.style.transform = this.pdfConfig.zoom !== 1 ? `scale(${this.pdfConfig.zoom})` : ''
        contentElement.style.transformOrigin = 'top left'

        // Wait for rendering
        await this.waitForImages(contentElement)
        await new Promise(resolve => setTimeout(resolve, 100))

        // Generate canvas
        const canvasWidthPx = containerWidthMm * 3.78 * this.pdfConfig.zoom
        const canvas = await html2canvas(contentElement, {
          scale: this.pdfConfig.scale,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: canvasWidthPx,
          height: undefined,
        })

        // Create PDF
        // eslint-disable-next-line new-cap
        const pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'mm',
          format: this.size === 'a4' ? 'a4' : [105, 148],
        })

        const imgData = canvas.toDataURL('image/png')
        const contentHeightMm = (canvas.height / (this.pdfConfig.scale * 3.78))

        if (this.size === 'a4') {
          pdf.addImage(imgData, 'PNG', 0, 0, targetWidthMm, contentHeightMm)
        } else {
          pdf.addImage(imgData, 'PNG', 0, 0, targetWidthMm, contentHeightMm)
        }

        return pdf
      } catch (error) {
        return null
      }
    },

    // Alternative method: print using existing iframe
    printPDFFromIframe() {
      if (this.pdfConfig.useIframePrint) {
        const iframe = this.$el.querySelector('.pdf-viewer')
        if (iframe && iframe.contentWindow) {
          try {
            // Try to print the iframe content directly
            iframe.contentWindow.print()
            return // Success, exit early
          } catch (error) {
            alert('Cannot print iframe directly, using fallback method')
          }
        }
      }

      // Fallback to direct PDF generation
      this.printPDF()
    },

    // Method to get the current PDF blob for direct download
    downloadPDF() {
      if (this.pdfUrl) {
        const link = document.createElement('a');
        [link.href] = this.pdfUrl.split('#')
        link.download = `packing-label-${this.data.invoice_number || 'label'}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.pdf-config-controls {
  h6 {
    margin-bottom: 10px;
    color: #495057;
  }

  .small {
    font-size: 0.875rem;
    font-weight: 500;
    color: #6c757d;
  }

  .form-control-range {
    margin-top: 5px;
  }
}

.pdf-container {
  min-height: 500px;

  .pdf-viewer {
    width: 100%;
    height: 500px;
    border: none; // Remove border for clean look
    border-radius: 4px;
    background: #f8f9fa; // Light background while loading
    overflow: hidden; // Hide any scrollbars
  }
}

.pdf-hidden-content {
  position: absolute;
  left: -9999px;
  top: -9999px;
  background: white;
  // Width will be set dynamically by JavaScript to prevent overflow
}

.label-container {
  border: 2px dotted #888888;
  padding: 12px; // Static padding inside the container
  font-family: "Montserrat", Helvetica, Arial, serif;
  color: #444444;
  background: white;
  box-sizing: border-box;
  width: 100%; // Take full width of dynamically-sized parent container
  height: auto; // Let content determine height
}

.label-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 8px;

  .header-left {
    .logo {
      width: 120px;
      height: auto;
      margin-bottom: 4px;
    }

    .invoice-number {
      margin: 0;
      font-size: 14px;
    }
  }

  .header-right {
    text-align: right;

    .courier-name {
      font-weight: 500;
      text-transform: uppercase;
      font-size: 14px;
    }

    .service-name {
      font-size: 14px;
    }

    .insurance {
      margin-top: 4px;

      small {
        font-size: 12px;
      }
    }
  }
}

.spacer {
  margin: 8px 0;
  border: none;
  border-top: 1px solid #ccc;
}

.barcode-section {
  text-align: center;

  .barcode-label {
    font-size: 14px;
  }

  .barcode-number {
    font-family: monospace;
    font-size: 14px;
    letter-spacing: 1px;
    font-weight: 500;
  }
}

.main-content {
  .address-section {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;

    .to-address {
      width: 58%;
      padding-right: 8px;

      h6 {
        margin: 0 0 4px 0;
        font-size: 14px;
        font-weight: 500;
      }

      .recipient-name {
        font-weight: 500;
        margin-bottom: 4px;
        font-size: 14px;
      }

      .recipient-address {
        font-size: 12px;
      }
    }

    .from-address {
      width: 40%;

      h6 {
        margin: 0 0 4px 0;
        font-size: 14px;
        font-weight: 500;
      }

      .sender-name {
        font-weight: 500;
        margin-bottom: 4px;
        font-size: 14px;
      }

      .sender-address {
        font-size: 12px;
      }
    }
  }
}

.products-section {
  margin-bottom: 10px;

  .products-table {
    width: 100%;
    border-collapse: collapse;

    th {
      text-align: left;
      padding: 0;
      font-size: 14px;
      font-weight: 500;

      &:last-child {
        text-align: right;
      }
    }

    td {
      padding: 1px 0;
      vertical-align: top;

      &.quantity {
        text-align: right;
        font-size: 14px;
      }
    }

    .product-item {
      display: flex;

      .item-number {
        margin-right: 4px;
        font-size: 14px;
      }

      .product-details {
        .product-name {
          font-weight: 500;
          font-size: 14px;
        }

        .product-attr,
        .product-sku {
          font-size: 14px;
        }
      }
    }
  }
}

.note-section {
  margin-top: 10px;

  p {
    margin: 0;
    font-size: 12px;
  }

  .gift-wrap-container {
    border: 1px solid #d8d6de;
    padding: 8px 10px;
    border-radius: 4px;
    display: flex;
    align-items: flex-start;
    background-color: #fafafb;
    color: #5e5873;
    margin-bottom: 8px;
    font-size: 12px;

    svg {
      margin-top: 1px;
      min-width: 16px;
      min-height: 16px;
      display: block;
      flex-shrink: 0;
    }

    span {
      line-height: 1.3;
    }
  }
}
</style>
